import clsx from 'clsx';
import { MdAdd } from 'react-icons/md';

import type { CandidateRecipientSelection } from '../hooks/useCandidateRecipientSelection';
import { recipientLabel, toRecipient } from '../lib/recipients';
import MailRecipientChips from './MailRecipientChips';
import MailRecipientsDropdown, { type MailRecipientsDropdownItem } from './MailRecipientsDropdown';

type MailRecipientFieldProps = {
  selection: CandidateRecipientSelection;
  variant: 'picker' | 'dropdown';
  disabled?: boolean;
  note?: string | null;
};

function selectableItems(selection: CandidateRecipientSelection): MailRecipientsDropdownItem[] {
  const candidateIds = new Set(selection.candidates.map((candidate) => candidate.id));
  const recipientIds = new Set(selection.recipients.map((recipient) => recipient.id));
  const unlistedRecipients = selection.recipients.filter(
    (recipient) => !candidateIds.has(recipient.id)
  );
  return [
    ...unlistedRecipients.map((recipient) => ({
      key: recipient.id,
      label: recipientLabel(recipient),
      selected: true,
      onSelect: () => selection.removeRecipient(recipient.id),
    })),
    ...selection.candidates.map((candidate) => ({
      key: candidate.id,
      label: recipientLabel(toRecipient(candidate)),
      selected: recipientIds.has(candidate.id),
      onSelect: () => selection.toggleRecipient(candidate),
    })),
  ];
}

export default function MailRecipientField({
  selection,
  variant,
  disabled = false,
  note,
}: MailRecipientFieldProps) {
  const isSelectionDisabled = selection.mailRecipientLimit === 0 || disabled;
  const isLimitHighlighted = isSelectionDisabled || selection.isLimitReached;
  const isPicker = variant === 'picker';

  return (
    <div className="flex flex-col gap-xxs">
      <div
        className={clsx(
          'flex min-h-[54px] items-center gap-xs rounded-sm border border-solid border-border bg-white',
          isPicker ? 'pr-xs' : 'px-md'
        )}
      >
        {isPicker && (
          <button
            type="button"
            onClick={selection.openPicker}
            disabled={isSelectionDisabled}
            className="flex h-[42px] shrink-0 cursor-pointer items-center gap-xxs rounded-sm border-none bg-transparent px-sm text-button-md text-primary outline-hidden hover:bg-primary_lighten disabled:cursor-not-allowed disabled:opacity-50"
          >
            <MdAdd className="size-icon-xs" />
            送信先を選択
          </button>
        )}
        <MailRecipientChips
          recipients={selection.recipients}
          className={isPicker ? 'flex-1 gap-xxs' : 'gap-xs'}
        />
        {isPicker ? (
          selection.recipients.length > 0 && (
            <MailRecipientsDropdown
              items={selection.recipients.map((recipient) => ({
                key: recipient.id,
                label: recipientLabel(recipient),
                onRemove: () => selection.removeRecipient(recipient.id),
              }))}
            />
          )
        ) : (
          <MailRecipientsDropdown
            items={selectableItems(selection)}
            disabled={isSelectionDisabled}
            onOpenChange={(open) => (open ? selection.openPicker() : selection.closePicker())}
            onReachEnd={selection.loadMore}
            hasMore={selection.hasNextPage}
            isLoadingMore={selection.isFetchingNextPage}
            contentWidthClassName="w-[432px]"
          />
        )}
      </div>
      {selection.limitStatusText && (
        <p className={clsx('text-body-xs', isLimitHighlighted ? 'text-danger' : 'text-sub')}>
          {selection.limitStatusText}
        </p>
      )}
      {note && <p className="text-body-xs text-sub">{note}</p>}
    </div>
  );
}
